import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, Loader2, Linkedin, Twitter, Facebook, Instagram, MapPin, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inquiryTypes = [
  { value: 'speaking', label: 'Speaking Engagement' },
  { value: 'advisory', label: 'Advisory Services' },
  { value: 'media', label: 'Media Enquiry' },
  { value: 'partnership', label: 'Partnership Opportunity' },
  { value: 'collaboration', label: 'Collaboration' },
  { value: 'general', label: 'General Enquiry' }
];

const socialLinks = [
  { icon: Linkedin, href: 'https://www.linkedin.com/in/ogaba-joseph-attah-6a7851118', label: 'LinkedIn' },
  { icon: Twitter, href: 'https://x.com/attah_meddyx', label: 'X/Twitter' },
  { icon: Facebook, href: 'https://www.facebook.com/attah.ogabajoseph', label: 'Facebook' },
  { icon: Instagram, href: 'https://www.instagram.com/jo_attah/', label: 'Instagram' },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiry_type: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, inquiry_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.inquiry_type || !formData.subject || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, formData);
      setIsSubmitted(true);
      toast.success('Your message has been sent successfully!');
      setFormData({
        name: '',
        email: '',
        company: '',
        inquiry_type: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact Ogaba Joseph Attah"
        description="Contact Ogaba Joseph Attah for speaking engagements, advisory work, media enquiries, or collaboration opportunities in digital infrastructure intelligence."
        canonicalUrl="https://www.ogabajosephattah.com/contact"
      />
      
      <Navigation />
      
      <main className="pt-24 lg:pt-32">
        {/* Hero */}
        <section className="py-16 md:py-24" data-testid="contact-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
              {/* Left - Info */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-6">
                  Get in Touch
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] mb-6" data-testid="contact-title">
                  Contact Ogaba Joseph Attah
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  For speaking engagements, advisory work, media enquiries, or collaboration opportunities.
                </p>

                {/* Contact Info */}
                <div className="space-y-6 mb-8">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <a href="mailto:ogabajosephattah@gmail.com" className="font-medium hover:text-primary transition-colors" data-testid="contact-email">
                        ogabajosephattah@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium">United Kingdom & Nigeria</p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div>
                  <p className="text-sm text-muted-foreground mb-4">Connect on Social</p>
                  <div className="flex gap-3">
                    {socialLinks.map((social) => (
                      <a
                        key={social.label}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                        data-testid={`contact-social-${social.label.toLowerCase()}`}
                        aria-label={social.label}
                      >
                        <social.icon className="h-5 w-5" />
                      </a>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="mt-12 hidden lg:block"
                >
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-muted">
                    <img
                      src="https://customer-assets.emergentagent.com/job_682de1e5-c93e-434b-afcf-b22d5a4ba52c/artifacts/fafgh1k6_IMG-20241020-WA0050.jpg"
                      alt="Ogaba Joseph Attah"
                      className="w-full h-full object-cover"
                      data-testid="contact-image"
                    />
                  </div>
                </motion.div>
              </motion.div>

              {/* Right - Form */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {isSubmitted ? (
                  <div className="p-8 bg-card border border-border rounded-2xl text-center" data-testid="contact-success">
                    <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full w-fit mx-auto mb-6">
                      <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h2 className="text-2xl font-bold mb-4">Message Sent!</h2>
                    <p className="text-muted-foreground mb-6">
                      Thank you for reaching out. Your message has been received and you can expect a response soon.
                    </p>
                    <Button onClick={() => setIsSubmitted(false)} variant="outline" data-testid="send-another-btn">
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="p-8 bg-card border border-border rounded-2xl" data-testid="contact-form">
                    <h2 className="text-xl font-semibold mb-6">Send a Message</h2>
                    
                    <div className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your name"
                            required
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="you@example.com"
                            required
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="company">Company/Organisation</Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your company"
                            data-testid="contact-company-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="inquiry_type">Inquiry Type *</Label>
                          <Select value={formData.inquiry_type} onValueChange={handleSelectChange}>
                            <SelectTrigger data-testid="contact-inquiry-type-select">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {inquiryTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value} data-testid={`inquiry-type-${type.value}`}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="What is this about?"
                          required
                          data-testid="contact-subject-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Your message..."
                          rows={5}
                          required
                          data-testid="contact-message-input"
                        />
                      </div>

                      <Button type="submit" className="w-full" size="lg" disabled={isSubmitting} data-testid="contact-submit-btn">
                        {isSubmitting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Inquiry Types Info */}
        <section className="py-24 md:py-32 bg-muted/30" data-testid="inquiry-types-info">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
                How Can I Help?
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Whether you're looking for a speaker, advisor, or collaboration partner, I'm here to help.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: 'Speaking', desc: 'Keynotes, panels, and conference presentations on technology and infrastructure.' },
                { title: 'Advisory', desc: 'Strategic guidance for digital infrastructure, technology investments, and scaling.' },
                { title: 'Media', desc: 'Interviews, commentary, and expert insights on industry trends.' },
                { title: 'Partnership', desc: 'Business partnerships and joint venture opportunities.' },
                { title: 'Collaboration', desc: 'Project collaborations and ecosystem building initiatives.' },
                { title: 'General', desc: 'Any other inquiries or just to say hello.' }
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 bg-card border border-border rounded-xl"
                  data-testid={`inquiry-info-${index}`}
                >
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
